import { debounce } from "lodash";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { Tab } from "../components/NavigationTabs";
import { cn } from "../utils/styling";

type UseDebouncedInputValueProps = {
  debounceTime: number;
  setValue: (newValue: unknown) => void;
  value: unknown;
};

export const useDebouncedInputValueWithSetValue = ({ debounceTime, setValue, value }: UseDebouncedInputValueProps) => {
  const [internalValue, setInternalValue] = useState(value);
  const debouncedOnChange = useCallback(
    debounce((newValue) => setValue?.(newValue), debounceTime),
    [debounceTime, setValue]
  );
  const handleChange = (newValue: unknown) => {
    if (!debounceTime) setValue?.(newValue);
    else {
      setInternalValue(newValue);
      debouncedOnChange(newValue);
    }
  };

  useEffect(() => setInternalValue(value), [value]);

  return { handleChange, inputValue: debounceTime ? internalValue : value };
};

type UseDebouncedInputValueWithOnChangeProps = {
  debounceTime: number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  value: any;
};

export const useDebouncedInputValueWithOnChange = ({
  debounceTime,
  onChange,
  value,
}: UseDebouncedInputValueWithOnChangeProps) => {
  const [internalValue, setInternalValue] = useState(value);
  const debouncedOnChange = useCallback(
    debounce((e) => onChange?.(e), debounceTime),
    [debounceTime, onChange]
  );
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!debounceTime) onChange?.(e);
    else {
      setInternalValue(e.target.value);
      debouncedOnChange(e);
    }
  };

  useEffect(() => setInternalValue(value), [value]);

  return { handleChange, inputValue: debounceTime ? internalValue : value };
};

export const useDelayedTransitionedDisplay = (delay = 100, className?: string) => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, delay);

    return () => clearTimeout(timer);
  }, [delay]);

  return { className: cn("transition-all duration-300", visible ? "opacity-100" : "opacity-0", className) };
};

export const useTabs = (tabs: Tab[], defaultTab: Tab) => {
  const [activeTab, setActiveTab] = useState(defaultTab ?? tabs[0]);

  const handleTabChange = (tab: Tab) => {
    setActiveTab(tab);
    tab.onClick?.();
  };
  return { activeTab, handleTabChange, setActiveTab };
};

export const useEffectSkipFirst = (effect: () => void, deps: React.DependencyList) => {
  const firstRenderRef = useRef(true);
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    effect();
  }, deps);
};
